import React from 'react';
import {
  formatPrice,
  formatPricePerSqft,
} from '../../../../shared/utils/formatters/propertyFormatters';
import usePropertyDetails from '../queries/usePropertyDetails';
import { useBreakpoint } from '../../../../shared/hooks/useBreakpoint';
import { TableViewCardSkeleton } from './skeleton/TableViewCardSkeleton';
import { FavoriteButton } from '../../../../components/common/FavoriteButton';

function belowCardContentlistPrice(
  listPrice: number,
  pricePerSqft: number,
  avm: number,
  avmPerSqft: number,
  equity: number
) {
  const labelClass = 'text-xs text-gray-500 uppercase';
  const valueClass = 'text-base font-bold';
  const containerClass = 'flex flex-col h-full border-r-2 border-gray-200';
  const lvContentContainerClass = 'flex flex-col p-2 justify-center';
  const largelvContentContainerClass =
    'flex flex-col justify-center items-center h-full';

  return (
    <div id="tvc-values" className="grid grid-cols-3 py-3 gap-4">
      <div id="tvc-content-body-left-container" className={containerClass}>
        <div
          id="tvc-content-body-list-price"
          className={lvContentContainerClass}
        >
          <div id="tvc-content-body-list-price-label" className={labelClass}>
            List Price
          </div>
          <div id="tvc-content-body-list-price-value" className={valueClass}>
            {formatPrice(listPrice)}
          </div>
        </div>
        <div
          id="tvc-content-body-price-per-sqft"
          className={lvContentContainerClass}
        >
          <div
            id="tvc-content-body-price-per-sqft-label"
            className={labelClass}
          >
            Price / sqft
          </div>
          <div
            id="tvc-content-body-price-per-sqft-value"
            className={valueClass}
          >
            {formatPricePerSqft(pricePerSqft)}
          </div>
        </div>
      </div>
      <div id="tvc-content-body-avm-container" className={containerClass}>
        <div id="tvc-content-body-avm" className={lvContentContainerClass}>
          <div id="tvc-content-body-avm-label" className={labelClass}>
            AVM
          </div>
          <div id="tvc-content-body-avm-value" className={valueClass}>
            {formatPrice(avm)}
          </div>
        </div>
        <div
          id="tvc-content-body-avm-per-sqft"
          className={lvContentContainerClass}
        >
          <div id="tvc-content-body-avm-per-sqft-label" className={labelClass}>
            Avm / Sqft{' '}
          </div>
          <div id="tvc-content-body-avm-per-sqft-value" className={valueClass}>
            {formatPricePerSqft(avmPerSqft)}
          </div>
        </div>
      </div>
      <div
        id="tvc-content-body-potential-equity-container"
        className={largelvContentContainerClass}
      >
        <div
          id="tvc-content-body-potential-equity-label"
          className={`${labelClass}`}
        >
          Potential Equity
        </div>
        <div
          id="tvc-content-body-potential-equity-value"
          className="text-xl font-bold text-gray-700 font-inter"
        >
          {formatPrice(equity)}
        </div>
      </div>
    </div>
  );
}

function belowCardContentlistPriceMobile(
  listPrice: number,
  pricePerSqft: number,
  avm: number,
  avmPerSqft: number,
  equity: number
) {
  const labelClass = 'text-xs text-gray-500 uppercase';
  const valueClass = 'text-base font-bold';
  const lvContentContainerMobileClass = 'flex flex-col pt-2 justify-center';
  const largelvContentContainerMobileClass =
    'flex flex-col pt-2 justify-center items-center';

  return (
    <div id="tvc-values-mobile">
      <div
        id="tvc-content-body-list-price"
        className={lvContentContainerMobileClass}
      >
        <div id="tvc-content-body-list-price-label" className={labelClass}>
          List Price
        </div>
        <div id="tvc-content-body-list-price-value" className={valueClass}>
          {formatPrice(listPrice)}
        </div>
      </div>
      <div
        id="tvc-content-body-price-per-sqft"
        className={lvContentContainerMobileClass}
      >
        <div id="tvc-content-body-price-per-sqft-label" className={labelClass}>
          Price / sqft
        </div>
        <div id="tvc-content-body-price-per-sqft-value" className={valueClass}>
          {formatPricePerSqft(pricePerSqft)}
        </div>
      </div>

      <div id="tvc-content-body-avm" className={lvContentContainerMobileClass}>
        <div id="tvc-content-body-avm-label" className={labelClass}>
          AVM
        </div>
        <div id="tvc-content-body-avm-value" className={valueClass}>
          {formatPrice(avm)}
        </div>
      </div>
      <div
        id="tvc-content-body-avm-per-sqft"
        className={lvContentContainerMobileClass}
      >
        <div id="tvc-content-body-avm-per-sqft-label" className={labelClass}>
          Avm / Sqft{' '}
        </div>
        <div id="tvc-content-body-avm-per-sqft-value" className={valueClass}>
          {formatPricePerSqft(avmPerSqft)}
        </div>
      </div>

      <div
        id="tvc-content-body-potential-equity-container"
        className={largelvContentContainerMobileClass}
      >
        <div
          id="tvc-content-body-potential-equity-label"
          className={`${labelClass}`}
        >
          Potential Equity
        </div>
        <div
          id="tvc-content-body-potential-equity-value"
          className="text-xl font-bold text-gray-700 font-inter"
        >
          {formatPrice(equity)}
        </div>
      </div>
    </div>
  );
}

function ListingSpecs({
  taxes,
  taxAssessed,
  daysOnMarket,
  yearBuilt,
  subdivision,
  garage,
}: {
  taxes: number;
  taxAssessed: number;
  daysOnMarket: number;
  yearBuilt: number;
  subdivision: string;
  garage: number;
}) {
  const containerClass =
    'flex items-center justify-between px-4 py-2 border-b-2 border-gray-200';
  const labelClass = 'text-gray-500 font-bold tracking-wide uppercase text-sm';
  const valueClass = 'text-gray-900 font-semibold';
  return (
    <>
      <div
        id="tvc-spec-taxes"
        data-testid="tvc-spec-taxes"
        className={containerClass}
      >
        <span className={labelClass}>ANNUAL TAXES:</span>
        <span className={valueClass}>{formatPrice(taxes)}</span>
      </div>
      <div
        id="tvc-spec-tax-assessed"
        data-testid="tvc-spec-tax-assessed"
        className={containerClass}
      >
        <span className={labelClass}>TAX ASSESSED VALUE:</span>
        <span className={valueClass}>{formatPrice(taxAssessed)}</span>
      </div>
      <div
        id="tvc-spec-days"
        data-testid="tvc-spec-days"
        className={containerClass}
      >
        <span className={labelClass}>DAYS ON MARKET:</span>
        <span className={valueClass}>{daysOnMarket}</span>
      </div>
      <div
        id="tvc-spec-year"
        data-testid="tvc-spec-year"
        className={containerClass}
      >
        <span className={labelClass}>YEAR BUILT:</span>
        <span className={valueClass}>{yearBuilt}</span>
      </div>
      <div
        id="tvc-spec-subdivision"
        data-testid="tvc-spec-subdivision"
        className={containerClass + ' items-start'}
      >
        <span className={labelClass}>SUBDIVISION:</span>
        <span className={valueClass + ' text-xs pl-1 text-right w-full block'}>
          {subdivision}
        </span>
      </div>
      <div
        id="tvc-spec-garage"
        data-testid="tvc-spec-garage"
        className={containerClass}
      >
        <span className={labelClass}>GARAGE:</span>
        <span className={valueClass}>{garage}</span>
      </div>
    </>
  );
}

export function TableViewCard({ id }: { id: string }) {
  const {
    propertyDetails: property,
    isLoadingPropertyDetails,
    errorPropertyDetails,
  } = usePropertyDetails(id);
  const { isXs } = useBreakpoint();

  if (errorPropertyDetails) {
    console.error('property id not found', id);
    // TODO: add a nice error visual state
    return null;
  }

  if (isLoadingPropertyDetails) {
    return <TableViewCardSkeleton />;
  }

  return (
    <article
      id="table-view-card"
      className="shadow-sm border p-3 bg-white flex flex-col gap-3 w-full"
    >
      <div
        id="tvc-header"
        className="flex justify-between gap-2 pr-2 pb-2 border-b-2 border-gray-200"
      >
        <div className="font-semibold text-lg flex-1 leading-none xs:leading-normal">
          {property.address}
        </div>
        <div className="text-sm font-bold text-gray-600 flex flex-1 items-center justify-around sm:flex-row md:flex-row flex-col">
          <div>{property.beds || '-'} beds</div>
          <div>{property.baths || '-'} baths</div>
          <div>{property.sqft || '-'} sq ft</div>
        </div>
      </div>

      <div id="tvc-body-container" className="max-h-[381px] h-full flex w-full">
        <div
          id="tvc-content-left"
          className="flex flex-col justify-center items-center max-w-[400px] pr-0 sm:pr-6"
        >
          <img
            src={property.imgUrl}
            alt={property.address}
            className="w-[400px] h-[268px] object-cover rounded-none"
          />
          {/* listPrice: number, pricePerSqft: number, avm: number, avmPerSqft: number, potentialEquity: number */}
          {isXs
            ? belowCardContentlistPriceMobile(
                property.listPrice,
                property.listPricePerSqft,
                property.avm,
                property.avmPerSqft,
                property.equity
              )
            : belowCardContentlistPrice(
                property.listPrice,
                property.listPricePerSqft,
                property.avm,
                property.avmPerSqft,
                property.equity
              )}
        </div>

        <div id="tvc-content-right" className="flex flex-1 flex-col ml-4 p-3">
          <div
            id="tvc-right-cta"
            className="grid grid-cols-2 mb-4 gap-6 w-full h-10"
          >
            <>
              <a
                id="tvc-cta-view-listing-mobile"
                onClick={() =>
                  window.open(`/properties/${property.id}`, '_blank')
                }
                className="sm:hidden flex cursor-pointer items-center justify-center rounded-xl bg-indigo-600 px-5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
              >
                View Listing
              </a>
              <a
                id="tvc-cta-view-listing-desktop"
                onClick={() =>
                  window.open(`/properties/${property.id}`, '_blank')
                }
                className="cursor-pointer text-base hidden sm:flex items-center justify-center rounded-xl bg-indigo-600 px-5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
              >
                View Full Listing
              </a>
            </>

            <FavoriteButton propertyId={property.id} />
          </div>
          <div id="tvc-specs">
            <ListingSpecs
              taxes={property.annualTaxes}
              taxAssessed={property.taxAssessedValue}
              daysOnMarket={property.daysOnMarket}
              yearBuilt={property.yearBuilt}
              subdivision={property.subdivision}
              garage={property.garage}
            />
          </div>
        </div>
      </div>
    </article>
  );
}
